import { ADMIN_TICKETS_API, INTERNAL_TICKETS_API, TICKETS_API, myTicketListSchema, orgTicketCodeSchema, orgTicketRoutingSchema, ticketAttachmentDownloadSchema, ticketAttachmentFinalizeSchema, ticketAttachmentPresignSchema, ticketAssigneeListSchema, ticketCustomerViewSchema, ticketLabelAdminSchema, ticketLabelListSchema, ticketListSchema, ticketMessageResultSchema, ticketPeopleSchema, ticketSettingsSchema, ticketStaffQueueSchema, ticketStaffViewSchema, } from '@traceio-org/platform-contracts';
/**
 * Which contract route and verb each ticket method serves, so a test holds the client to the contract's route tables
 * (one method per route and verb).
 */
export const TICKET_CLIENT_METHODS = {
    tickets: { api: 'hub', method: 'GET', route: 'list' },
    createTicket: { api: 'hub', method: 'POST', route: 'list' },
    ticket: { api: 'hub', method: 'GET', route: 'ticket' },
    replyToTicket: { api: 'hub', method: 'POST', route: 'reply' },
    ticketAction: { api: 'hub', method: 'POST', route: 'action' },
    myTickets: { api: 'hub', method: 'GET', route: 'mine' },
    ticketRouting: { api: 'hub', method: 'GET', route: 'routing' },
    setTicketRouting: { api: 'hub', method: 'PUT', route: 'routing' },
    ticketPeople: { api: 'hub', method: 'GET', route: 'people' },
    presignTicketAttachment: { api: 'hub', method: 'POST', route: 'presign' },
    finalizeTicketAttachment: { api: 'hub', method: 'POST', route: 'finalize' },
    ticketAttachmentDownload: { api: 'hub', method: 'GET', route: 'download' },
    adminTicketQueue: { api: 'admin', method: 'GET', route: 'queue' },
    adminCreateTicket: { api: 'admin', method: 'POST', route: 'create' },
    adminTicket: { api: 'admin', method: 'GET', route: 'ticket' },
    adminUpdateTicket: { api: 'admin', method: 'PATCH', route: 'update' },
    adminTicketReply: { api: 'admin', method: 'POST', route: 'reply' },
    adminTicketHandBack: { api: 'admin', method: 'POST', route: 'handBack' },
    adminAddTicketLink: { api: 'admin', method: 'POST', route: 'links' },
    adminRemoveTicketLink: { api: 'admin', method: 'DELETE', route: 'link' },
    adminTicketLabels: { api: 'admin', method: 'GET', route: 'labels' },
    adminCreateTicketLabel: { api: 'admin', method: 'POST', route: 'labels' },
    adminUpdateTicketLabel: { api: 'admin', method: 'PATCH', route: 'label' },
    adminTicketAssignees: { api: 'admin', method: 'GET', route: 'assignees' },
    adminTicketSettings: { api: 'admin', method: 'GET', route: 'settings' },
    adminUpdateTicketSettings: { api: 'admin', method: 'PUT', route: 'settings' },
    adminSetOrgTicketCode: { api: 'admin', method: 'PUT', route: 'orgTicketCode' },
    adminPresignTicketAttachment: { api: 'admin', method: 'POST', route: 'presign' },
    adminFinalizeTicketAttachment: { api: 'admin', method: 'POST', route: 'finalize' },
    adminTicketAttachmentDownload: { api: 'admin', method: 'GET', route: 'download' },
    internalCreateTicket: { api: 'internal', method: 'POST', route: 'create' },
    internalMyTickets: { api: 'internal', method: 'GET', route: 'listMine' },
    internalTicket: { api: 'internal', method: 'GET', route: 'ticket' },
    internalTicketReply: { api: 'internal', method: 'POST', route: 'reply' },
    internalPresignTicketAttachment: { api: 'internal', method: 'POST', route: 'presign' },
    internalFinalizeTicketAttachment: { api: 'internal', method: 'POST', route: 'finalize' },
    internalTicketAttachmentDownload: { api: 'internal', method: 'GET', route: 'download' },
};
/** Route templates for error reports (the contract's builders make the paths). */
const ROUTES = {
    list: '/v1/orgs/:organizationId/tickets',
    ticket: '/v1/orgs/:organizationId/tickets/:number',
    reply: '/v1/orgs/:organizationId/tickets/:number/messages',
    action: '/v1/orgs/:organizationId/tickets/:number/:action',
    adminTicket: '/v1/admin/tickets/:number',
    adminLink: '/v1/admin/tickets/:number/links/:linkId',
    adminLabel: '/v1/admin/ticket-labels/:labelId',
    adminOrgTicketCode: '/v1/admin/organizations/:organizationId/ticket-code',
    internalTicket: '/v1/internal/tickets/:number',
    attachments: '/v1/orgs/:organizationId/ticket-attachments',
    routing: '/v1/orgs/:organizationId/ticket-routing',
    people: '/v1/orgs/:organizationId/ticket-people',
    attachment: '/v1/orgs/:organizationId/ticket-attachments/:attachmentId',
    adminAttachment: '/v1/admin/ticket-attachments/:attachmentId',
    internalAttachment: '/v1/internal/tickets/attachments/:attachmentId',
};
const list = (values) => values && values.length > 0 ? values.join(',') : undefined;
export function createTicketMethods(request) {
    const call = (spec, options) => request({ ...spec, auth: 'bearer', signal: options?.signal, accessToken: options?.accessToken });
    const actorQuery = (actor) => ({
        organizationId: actor.organizationId,
        userId: actor.userId,
        product: actor.product,
    });
    return {
        tickets: (organizationId, query, options) => call({
            route: ROUTES.list,
            path: TICKETS_API.list(organizationId),
            method: 'GET',
            query: {
                status: list(query?.status),
                assignee: query?.assignee,
                product: query?.product,
                q: query?.q,
                cursor: query?.cursor,
                limit: query?.limit,
            },
            schema: ticketListSchema,
        }, options),
        createTicket: (organizationId, body, options) => call({
            route: ROUTES.list,
            path: TICKETS_API.list(organizationId),
            method: 'POST',
            body,
            schema: ticketCustomerViewSchema,
        }, options),
        ticket: (organizationId, number, options) => call({
            route: ROUTES.ticket,
            path: TICKETS_API.ticket(organizationId, number),
            method: 'GET',
            schema: ticketCustomerViewSchema,
        }, options),
        replyToTicket: (organizationId, number, body, options) => call({
            route: ROUTES.reply,
            path: TICKETS_API.reply(organizationId, number),
            method: 'POST',
            body,
            schema: ticketMessageResultSchema,
        }, options),
        ticketAction: (organizationId, number, action, body, options) => call({
            route: ROUTES.action,
            path: TICKETS_API.action(organizationId, number, action),
            method: 'POST',
            body,
            schema: ticketCustomerViewSchema,
        }, options),
        presignTicketAttachment: (organizationId, body, options) => call({
            route: ROUTES.attachments,
            path: TICKETS_API.presign(organizationId),
            method: 'POST',
            body,
            schema: ticketAttachmentPresignSchema,
        }, options),
        finalizeTicketAttachment: (organizationId, attachmentId, options) => call({
            route: `${ROUTES.attachment}/finalize`,
            path: TICKETS_API.finalize(organizationId, attachmentId),
            method: 'POST',
            schema: ticketAttachmentFinalizeSchema,
        }, options),
        ticketAttachmentDownload: (organizationId, attachmentId, options) => call({
            route: ROUTES.attachment,
            path: TICKETS_API.download(organizationId, attachmentId),
            method: 'GET',
            schema: ticketAttachmentDownloadSchema,
        }, options),
        myTickets: (options) => call({ route: TICKETS_API.mine, path: TICKETS_API.mine, method: 'GET', schema: myTicketListSchema }, options),
        ticketRouting: (organizationId, options) => call({
            route: ROUTES.routing,
            path: TICKETS_API.routing(organizationId),
            method: 'GET',
            schema: orgTicketRoutingSchema,
        }, options),
        setTicketRouting: (organizationId, body, options) => call({
            route: ROUTES.routing,
            path: TICKETS_API.routing(organizationId),
            method: 'PUT',
            body,
            schema: orgTicketRoutingSchema,
        }, options),
        ticketPeople: (organizationId, options) => call({
            route: ROUTES.people,
            path: TICKETS_API.people(organizationId),
            method: 'GET',
            schema: ticketPeopleSchema,
        }, options),
        adminTicketQueue: (query, options) => call({
            route: ADMIN_TICKETS_API.queue,
            path: ADMIN_TICKETS_API.queue,
            method: 'GET',
            query: {
                view: query?.view,
                status: list(query?.status),
                priority: list(query?.priority),
                labelIds: list(query?.labelIds),
                assignee: query?.assignee,
                orgId: query?.orgId,
                product: query?.product,
                overdue: query?.overdue === undefined ? undefined : String(query.overdue),
                q: query?.q,
                olderThanDays: query?.olderThanDays,
                cursor: query?.cursor,
                limit: query?.limit,
            },
            schema: ticketStaffQueueSchema,
        }, options),
        adminCreateTicket: (body, options) => call({
            route: ADMIN_TICKETS_API.create,
            path: ADMIN_TICKETS_API.create,
            method: 'POST',
            body,
            schema: ticketStaffViewSchema,
        }, options),
        adminTicket: (number, options) => call({
            route: ROUTES.adminTicket,
            path: ADMIN_TICKETS_API.ticket(number),
            method: 'GET',
            schema: ticketStaffViewSchema,
        }, options),
        adminUpdateTicket: (number, body, options) => call({
            route: ROUTES.adminTicket,
            path: ADMIN_TICKETS_API.update(number),
            method: 'PATCH',
            body,
            schema: ticketStaffViewSchema,
        }, options),
        adminTicketReply: (number, body, options) => call({
            route: `${ROUTES.adminTicket}/messages`,
            path: ADMIN_TICKETS_API.reply(number),
            method: 'POST',
            body,
            schema: ticketMessageResultSchema,
        }, options),
        adminTicketHandBack: (number, body, options) => call({
            route: `${ROUTES.adminTicket}/hand-back`,
            path: ADMIN_TICKETS_API.handBack(number),
            method: 'POST',
            body,
            schema: ticketStaffViewSchema,
        }, options),
        adminAddTicketLink: (number, body, options) => call({
            route: `${ROUTES.adminTicket}/links`,
            path: ADMIN_TICKETS_API.links(number),
            method: 'POST',
            body,
            schema: ticketStaffViewSchema,
        }, options),
        adminRemoveTicketLink: (number, linkId, options) => call({
            route: ROUTES.adminLink,
            path: ADMIN_TICKETS_API.link(number, linkId),
            method: 'DELETE',
            schema: ticketStaffViewSchema,
        }, options),
        adminTicketLabels: (options) => call({
            route: ADMIN_TICKETS_API.labels,
            path: ADMIN_TICKETS_API.labels,
            method: 'GET',
            schema: ticketLabelListSchema,
        }, options),
        adminCreateTicketLabel: (body, options) => call({
            route: ADMIN_TICKETS_API.labels,
            path: ADMIN_TICKETS_API.labels,
            method: 'POST',
            body,
            schema: ticketLabelAdminSchema,
        }, options),
        adminUpdateTicketLabel: (labelId, body, options) => call({
            route: ROUTES.adminLabel,
            path: ADMIN_TICKETS_API.label(labelId),
            method: 'PATCH',
            body,
            schema: ticketLabelAdminSchema,
        }, options),
        adminTicketAssignees: (options) => call({
            route: ADMIN_TICKETS_API.assignees,
            path: ADMIN_TICKETS_API.assignees,
            method: 'GET',
            schema: ticketAssigneeListSchema,
        }, options),
        adminTicketSettings: (options) => call({
            route: ADMIN_TICKETS_API.settings,
            path: ADMIN_TICKETS_API.settings,
            method: 'GET',
            schema: ticketSettingsSchema,
        }, options),
        adminUpdateTicketSettings: (body, options) => call({
            route: ADMIN_TICKETS_API.settings,
            path: ADMIN_TICKETS_API.settings,
            method: 'PUT',
            body,
            schema: ticketSettingsSchema,
        }, options),
        adminSetOrgTicketCode: (organizationId, body, options) => call({
            route: ROUTES.adminOrgTicketCode,
            path: ADMIN_TICKETS_API.orgTicketCode(organizationId),
            method: 'PUT',
            body,
            schema: orgTicketCodeSchema,
        }, options),
        adminPresignTicketAttachment: (body, options) => call({
            route: ADMIN_TICKETS_API.presign,
            path: ADMIN_TICKETS_API.presign,
            method: 'POST',
            body,
            schema: ticketAttachmentPresignSchema,
        }, options),
        adminFinalizeTicketAttachment: (attachmentId, options) => call({
            route: `${ROUTES.adminAttachment}/finalize`,
            path: ADMIN_TICKETS_API.finalize(attachmentId),
            method: 'POST',
            schema: ticketAttachmentFinalizeSchema,
        }, options),
        adminTicketAttachmentDownload: (attachmentId, options) => call({
            route: ROUTES.adminAttachment,
            path: ADMIN_TICKETS_API.download(attachmentId),
            method: 'GET',
            schema: ticketAttachmentDownloadSchema,
        }, options),
        internalCreateTicket: (body, options) => call({
            route: INTERNAL_TICKETS_API.create,
            path: INTERNAL_TICKETS_API.create,
            method: 'POST',
            body,
            schema: ticketCustomerViewSchema,
        }, options),
        internalMyTickets: (actor, options) => call({
            route: INTERNAL_TICKETS_API.listMine,
            path: INTERNAL_TICKETS_API.listMine,
            method: 'GET',
            query: actorQuery(actor),
            schema: myTicketListSchema,
        }, options),
        internalTicket: (number, actor, options) => call({
            route: ROUTES.internalTicket,
            path: INTERNAL_TICKETS_API.ticket(number),
            method: 'GET',
            query: actorQuery(actor),
            schema: ticketCustomerViewSchema,
        }, options),
        internalTicketReply: (number, body, options) => call({
            route: `${ROUTES.internalTicket}/messages`,
            path: INTERNAL_TICKETS_API.reply(number),
            method: 'POST',
            body,
            schema: ticketMessageResultSchema,
        }, options),
        internalPresignTicketAttachment: (body, options) => call({
            route: INTERNAL_TICKETS_API.presign,
            path: INTERNAL_TICKETS_API.presign,
            method: 'POST',
            body,
            schema: ticketAttachmentPresignSchema,
        }, options),
        internalFinalizeTicketAttachment: (attachmentId, actor, options) => call({
            route: `${ROUTES.internalAttachment}/finalize`,
            path: INTERNAL_TICKETS_API.finalize(attachmentId),
            method: 'POST',
            body: actorQuery(actor),
            schema: ticketAttachmentFinalizeSchema,
        }, options),
        internalTicketAttachmentDownload: (attachmentId, actor, options) => call({
            route: ROUTES.internalAttachment,
            path: INTERNAL_TICKETS_API.download(attachmentId),
            method: 'GET',
            query: actorQuery(actor),
            schema: ticketAttachmentDownloadSchema,
        }, options),
    };
}
//# sourceMappingURL=tickets.js.map